import { type RefCallback, useCallback, useEffect, useRef, useState } from "react"

const SCROLL_EDGE_TOLERANCE = 1
const WHEEL_SENSITIVITY = 0.8
const LEFT_MOUSE_BUTTON = 0
const DRAG_START_THRESHOLD_PX = 4
const RESET_CLICK_SUPPRESSION_DELAY_MS = 0

type DragState = {
  isPointerDown: boolean
  isDragging: boolean
  startX: number
  startScrollLeft: number
  suppressClick: boolean
}

const createInitialDragState = (): DragState => ({
  isPointerDown: false,
  isDragging: false,
  startX: 0,
  startScrollLeft: 0,
  suppressClick: false,
})

/**
 * Enables horizontal wheel scroll + drag-to-scroll behavior for wide carousels.
 * Ported from the legacy Today module to keep interaction parity.
 */
export function useHorizontalWheelScroll<T extends HTMLElement>(): RefCallback<T> {
  const [containerVersion, setContainerVersion] = useState(0)
  const containerRef = useRef<T | null>(null)
  const dragStateRef = useRef<DragState>(createInitialDragState())
  const suppressClickTimeoutRef = useRef<number | null>(null)

  const setContainer = useCallback((node: T | null) => {
    containerRef.current = node
    setContainerVersion((prev) => prev + 1)
  }, [])

  const handleWheel = useCallback((event: WheelEvent) => {
    const container = containerRef.current
    if (event.deltaY === 0 || !container) return

    const maxScrollLeft = container.scrollWidth - container.clientWidth
    if (maxScrollLeft <= 0) return

    const isScrollingRight = event.deltaY > 0
    const isAtStart = container.scrollLeft <= SCROLL_EDGE_TOLERANCE
    const isAtEnd = container.scrollLeft >= maxScrollLeft - SCROLL_EDGE_TOLERANCE
    const canScrollHorizontally = (isScrollingRight && !isAtEnd) || (!isScrollingRight && !isAtStart)

    if (!canScrollHorizontally) return

    event.preventDefault()
    container.scrollBy({
      left: event.deltaY * WHEEL_SENSITIVITY,
      behavior: "smooth",
    })
  }, [])

  const clearSuppressClickTimeout = useCallback(() => {
    if (suppressClickTimeoutRef.current === null) return

    window.clearTimeout(suppressClickTimeoutRef.current)
    suppressClickTimeoutRef.current = null
  }, [])

  const resetDragState = useCallback(() => {
    dragStateRef.current = createInitialDragState()
  }, [])

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      const container = containerRef.current
      if (event.button !== LEFT_MOUSE_BUTTON || !container) return

      const maxScrollLeft = container.scrollWidth - container.clientWidth
      if (maxScrollLeft <= 0) return

      clearSuppressClickTimeout()
      dragStateRef.current = {
        ...dragStateRef.current,
        isPointerDown: true,
        isDragging: false,
        startX: event.clientX,
        startScrollLeft: container.scrollLeft,
        suppressClick: false,
      }

      container.style.cursor = "grabbing"
    },
    [clearSuppressClickTimeout],
  )

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const container = containerRef.current
      if (!container) return

      const dragState = dragStateRef.current
      if (!dragState.isPointerDown) return

      const deltaX = event.clientX - dragState.startX
      if (!dragState.isDragging && Math.abs(deltaX) < DRAG_START_THRESHOLD_PX) return

      dragState.isDragging = true
      event.preventDefault()
      container.scrollLeft = dragState.startScrollLeft - deltaX
    },
    [],
  )

  const handleMouseUp = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const dragState = dragStateRef.current
    if (!dragState.isPointerDown) return

    dragState.isPointerDown = false
    container.style.cursor = ""

    if (!dragState.isDragging) return

    dragState.isDragging = false
    dragState.suppressClick = true

    clearSuppressClickTimeout()
    suppressClickTimeoutRef.current = window.setTimeout(() => {
      dragStateRef.current.suppressClick = false
      suppressClickTimeoutRef.current = null
    }, RESET_CLICK_SUPPRESSION_DELAY_MS)
  }, [clearSuppressClickTimeout])

  const handleClickCapture = useCallback(
    (event: MouseEvent) => {
      if (!dragStateRef.current.suppressClick) return

      event.preventDefault()
      event.stopPropagation()
      clearSuppressClickTimeout()
      dragStateRef.current.suppressClick = false
    },
    [clearSuppressClickTimeout],
  )

  const handleDragStart = useCallback((event: DragEvent) => {
    if (!dragStateRef.current.isPointerDown) return
    event.preventDefault()
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener("wheel", handleWheel, { passive: false })
    container.addEventListener("mousedown", handleMouseDown)
    container.addEventListener("click", handleClickCapture, true)
    container.addEventListener("dragstart", handleDragStart)
    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)
    window.addEventListener("blur", handleMouseUp)

    return () => {
      container.removeEventListener("wheel", handleWheel)
      container.removeEventListener("mousedown", handleMouseDown)
      container.removeEventListener("click", handleClickCapture, true)
      container.removeEventListener("dragstart", handleDragStart)
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
      window.removeEventListener("blur", handleMouseUp)
      container.style.cursor = ""
      clearSuppressClickTimeout()
      resetDragState()
    }
  }, [
    clearSuppressClickTimeout,
    containerVersion,
    handleClickCapture,
    handleDragStart,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    resetDragState,
  ])

  return setContainer
}
